import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { CLINIC, WHATSAPP_URL } from "@/data/clinic";
import SEOHead from "@/components/SEOHead";

/**
 * PQRS — Peticiones, quejas, reclamos y sugerencias.
 * Canal recomendado: correo de gerencia. Si no está definido aún, se muestra
 * el formulario con envío al backend estándar y se indica el canal alternativo.
 */
export default function Pqrs() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", type: "peticion", message: "", consent: false });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      toast.error("Por favor acepta el tratamiento de datos para continuar.");
      return;
    }
    setLoading(true);
    try {
      const { consent: _c, ...rest } = form;
      await api.post("/contact", { ...rest, message: `[PQRS · ${form.type}] ${form.message}` });
      toast.success("Recibimos tu solicitud. Te responderemos a la mayor brevedad.");
      setForm({ name: "", email: "", phone: "", type: "peticion", message: "", consent: false });
    } catch {
      toast.error("No pudimos enviar tu mensaje. Escríbenos por WhatsApp.");
    } finally {
      setLoading(false);
    }
  };

  const managementEmail = CLINIC.managementEmail;

  return (
    <section data-testid="pqrs-page" className="mx-auto max-w-3xl px-6 py-16 lg:py-24">
      <SEOHead
        title="PQRS · Katia Falón Odontología"
        description="Canal oficial de Peticiones, Quejas, Reclamos y Sugerencias de Katia Falón Odontología en Armenia, Quindío."
        path="/pqrs"
      />
      <div className="text-xs uppercase tracking-[0.3em] text-[hsl(var(--clinic-muted))]">Canal PQRS</div>
      <h1 className="mt-2 font-display text-4xl sm:text-5xl">Peticiones, quejas y sugerencias</h1>
      <p className="mt-4 text-sm leading-relaxed text-[hsl(var(--clinic-muted))]">
        Queremos escuchar cualquier situación que hayas vivido con nosotros. Este canal es directo con la
        gerencia y nos ayuda a mejorar la atención.
        {managementEmail ? (
          <> También puedes escribirnos directamente a <a href={`mailto:${managementEmail}`} className="underline">{managementEmail}</a>.</>
        ) : (
          <> Si prefieres un canal alternativo, escríbenos por <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="underline">WhatsApp</a>.</>
        )}
      </p>
      <form onSubmit={submit} className="mt-10 rounded-3xl border border-[hsl(var(--clinic-border))] bg-white p-8" data-testid="pqrs-form">
        <div className="grid gap-4">
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-widest text-[hsl(var(--clinic-muted))]">Tipo</span>
            <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="pqrs-type" className="mt-1 w-full rounded-xl border border-[hsl(var(--clinic-border))] bg-white px-4 py-3 text-sm">
              <option value="peticion">Petición</option>
              <option value="queja">Queja</option>
              <option value="reclamo">Reclamo</option>
              <option value="sugerencia">Sugerencia</option>
              <option value="felicitacion">Felicitación</option>
            </select>
          </label>
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-widest text-[hsl(var(--clinic-muted))]">Nombre completo</span>
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="pqrs-name" className="mt-1 w-full rounded-xl border border-[hsl(var(--clinic-border))] px-4 py-3 text-sm" />
          </label>
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-widest text-[hsl(var(--clinic-muted))]">Correo</span>
              <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="pqrs-email" className="mt-1 w-full rounded-xl border border-[hsl(var(--clinic-border))] px-4 py-3 text-sm" />
            </label>
            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-widest text-[hsl(var(--clinic-muted))]">Teléfono (opcional)</span>
              <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="pqrs-phone" className="mt-1 w-full rounded-xl border border-[hsl(var(--clinic-border))] px-4 py-3 text-sm" />
            </label>
          </div>
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-widest text-[hsl(var(--clinic-muted))]">Cuéntanos</span>
            <textarea required rows={6} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="pqrs-message" className="mt-1 w-full rounded-xl border border-[hsl(var(--clinic-border))] px-4 py-3 text-sm resize-none" />
          </label>
          <label className="flex items-start gap-3 text-xs text-[hsl(var(--clinic-muted))]">
            <input type="checkbox" required checked={form.consent} onChange={(e) => setForm({ ...form, consent: e.target.checked })} data-testid="pqrs-consent" className="mt-1 accent-[hsl(var(--clinic-secondary))]" />
            <span>Autorizo el tratamiento de mis datos personales según la <a href="/legal" className="underline">política de privacidad</a> para dar respuesta a esta solicitud.</span>
          </label>
          <button type="submit" disabled={loading} data-testid="pqrs-submit" className="mt-2 rounded-full bg-[hsl(var(--clinic-secondary))] px-6 py-3 text-sm font-semibold text-white disabled:opacity-50">
            {loading ? "Enviando…" : "Enviar solicitud"}
          </button>
        </div>
      </form>
    </section>
  );
}
